import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, LinearProgress, Skeleton, TextField, Typography,
} from '@mui/material';
import { MdEdit, MdLock, MdPhotoCamera, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { getData, postData, postFileUpload } from '../../Webservice/webservice';

const FONT = 'Mulish, sans-serif';
const yellowBtn = { textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', px: 3, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { background: 'rgb(255 190 0)' } };
const authToken = () => { const raw = sessionStorage.getItem('token'); return raw ? `Bearer ${raw}` : ''; };
const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const fmtTime = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const STATUS = { approved: ['Approved', '#dcfce7', '#166534'], waitingForApproval: ['Waiting for approval', '#fef3c7', '#92400e'], rejected: ['Rejected', '#fee2e2', '#991b1b'], pending: ['Incomplete', '#f1f5f9', '#475569'] };

const Card = ({ title, accent, right, children, tour }) => (
  <Box data-tour={tour} sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, borderRadius: '14px', p: 2.25, minWidth: 0 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
      <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 15 }}>{title}</Typography>
      {right}
    </Box>
    {children}
  </Box>
);
const Row = ({ label, value, mono }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.9, borderBottom: '1px dashed #e2e8f0', '&:last-of-type': { borderBottom: 'none' } }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b', flexShrink: 0 }}>{label}</Typography>
    <Typography component="div" sx={{ fontFamily: mono ? 'JetBrains Mono, monospace' : FONT, fontSize: 13.5, fontWeight: 700, textAlign: 'right', wordBreak: 'break-word' }}>{value || '—'}</Typography>
  </Box>
);

// 0-4: how hard a new password is to guess
const strength = (p) => {
  let s = 0;
  if (p.length >= 8) s += 1;
  if (p.length >= 12) s += 1;
  if (/[a-z]/.test(p) && /[A-Z]/.test(p)) s += 1;
  if (/\d/.test(p) && /[^A-Za-z0-9]/.test(p)) s += 1;
  return s;
};
const STRENGTH = [['Too short', '#ef4444'], ['Weak', '#f97316'], ['Fair', '#f59e0b'], ['Good', '#22c55e'], ['Strong', '#16a34a']];

function Profile() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [logins, setLogins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phoneNumber: '', file: null, preview: '' });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ oldPassword: '', newPassword: '', confirm: '' });
  const [show, setShow] = useState({ old: false, next: false });
  const [pwError, setPwError] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getData('profile/me', null, authToken());
      setMe(res.user);
      setLogins(res.recentLogins || []);
      setError('');
    } catch (e) {
      setError(e?.message || 'Could not load your profile.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEdit = () => {
    setForm({ name: me.name || '', email: me.email || '', phoneNumber: me.mobile || '', file: null, preview: me.image || '' });
    setFormError('');
    setEditOpen(true);
  };

  const pickPhoto = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!f.type.startsWith('image/')) return setFormError('Choose an image file.');
    if (f.size > 5 * 1024 * 1024) return setFormError('The photo must be under 5 MB.');
    setFormError('');
    setForm((x) => ({ ...x, file: f, preview: URL.createObjectURL(f) }));
  };

  const save = async () => {
    if (form.name.trim().length < 2) return setFormError('Enter your name.');
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return setFormError('Enter a valid email address.');
    if (form.phoneNumber && !/^(\+?91)?[6-9]\d{9}$/.test(form.phoneNumber.replace(/[\s-]/g, ''))) return setFormError('Enter a valid 10 digit mobile number.');
    setSaving(true);
    setFormError('');
    try {
      const body = new FormData();
      body.append('name', form.name.trim());
      body.append('email', form.email.trim());
      if (form.phoneNumber) body.append('phoneNumber', form.phoneNumber.trim());
      if (form.file) body.append('image', form.file);
      const res = await postFileUpload('profile/update', body, authToken());
      const next = res?.data || {};
      setMe((m) => ({ ...m, ...next }));
      // keep the top bar and other pages in step with what was saved
      ['user', 'sellerUser'].forEach((key) => {
        const store = key === 'user' ? sessionStorage : localStorage;
        try {
          const cur = JSON.parse(store.getItem(key) || 'null');
          if (cur) store.setItem(key, JSON.stringify({ ...cur, name: next.name ?? cur.name, email: next.email ?? cur.email, phoneNumber: next.mobile ?? cur.phoneNumber, image: next.image ?? cur.image }));
        } catch { /* a bad stored value is simply left alone */ }
      });
      window.dispatchEvent(new Event('seller-profile-changed')); // the top bar shows the new name and photo
      setEditOpen(false);
      setNotice('Your profile was updated.');
    } catch (e) {
      setFormError(e?.message || 'Could not save your profile.');
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (!pw.oldPassword) return setPwError('Enter your current password.');
    if (pw.newPassword.length < 8) return setPwError('The new password must be at least 8 characters.');
    if (pw.newPassword === pw.oldPassword) return setPwError('The new password must be different from the old one.');
    if (pw.newPassword !== pw.confirm) return setPwError('The two new passwords do not match.');
    setPwSaving(true);
    setPwError('');
    try {
      await postData('profile/update/password', { oldPassword: pw.oldPassword, newPassword: pw.newPassword }, authToken());
      setPwOpen(false);
      setPw({ oldPassword: '', newPassword: '', confirm: '' });
      setNotice('Your password was changed. Use it the next time you sign in.');
    } catch (e) {
      setPwError(e?.message || 'Could not change the password.');
    } finally {
      setPwSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1100, mx: 'auto' }}><Skeleton variant="rounded" height={150} sx={{ borderRadius: '16px', mb: 2 }} /><Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}><Skeleton variant="rounded" height={260} sx={{ borderRadius: '14px' }} /><Skeleton variant="rounded" height={260} sx={{ borderRadius: '14px' }} /></Box></Box>;
  }
  if (error || !me) {
    return <Box sx={{ p: 3, maxWidth: 560, mx: 'auto' }}><Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>{error || 'Your profile could not be found.'}</Alert></Box>;
  }

  const [statusLabel, statusBg, statusFg] = STATUS[me.status] || [me.status || 'Unknown', '#f1f5f9', '#475569'];
  const joinedAddress = (a, c, s, p) => [a, c, s, p].filter(Boolean).join(', ');
  const pwScore = strength(pw.newPassword);
  const [pwLabel, pwColor] = STRENGTH[pw.newPassword ? pwScore : 0];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1100, mx: 'auto', fontFamily: FONT }}>
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}

      {/* identity */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap', p: { xs: 2, md: 3 }, borderRadius: '18px', mb: 2, color: '#fff', background: 'linear-gradient(135deg,#6366f1,#4338ca)' }}>
        <Avatar src={me.image || undefined} sx={{ width: 92, height: 92, fontSize: 38, fontWeight: 800, bgcolor: 'rgb(255 209 2)', color: '#000', border: '4px solid rgba(255,255,255,.35)' }}>{(me.name || 'S').charAt(0).toUpperCase()}</Avatar>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: { xs: 22, md: 26 }, lineHeight: 1.2 }}>{me.name}</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 14, opacity: 0.92 }}>{me.business_name || 'Seller'}</Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
            {me.sellerCode && <Chip size="small" label={me.sellerCode} sx={{ fontFamily: FONT, fontWeight: 800, color: '#fff', background: 'rgba(255,255,255,.2)' }} />}
            <Chip size="small" label={statusLabel} sx={{ fontFamily: FONT, fontWeight: 800, background: statusBg, color: statusFg }} />
            <Chip size="small" label={`Seller since ${fmt(me.createdAt)}`} sx={{ fontFamily: FONT, fontWeight: 700, color: '#fff', background: 'rgba(255,255,255,.18)' }} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button data-tour="vendor-profile-edit" startIcon={<MdEdit />} onClick={openEdit} sx={yellowBtn}>Edit profile</Button>
          <Button data-tour="vendor-profile-password" startIcon={<MdLock />} onClick={() => { setPwOpen(true); setPwError(''); }} sx={{ textTransform: 'none', fontWeight: 800, color: '#fff', border: '1px solid rgba(255,255,255,.5)', borderRadius: '10px', px: 2.5 }}>Change password</Button>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, alignItems: 'start' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <Card tour="vendor-profile-details" title="Contact" accent="#6366f1" right={<Button size="small" onClick={openEdit} sx={{ textTransform: 'none', fontWeight: 700 }}>Edit</Button>}>
            <Row label="Name" value={me.name} />
            <Row label="Email (sign in)" value={me.email} />
            <Row label="Mobile" value={me.mobile} />
            {(me.contactPerson2Name || me.contactPerson2Email) && (
              <>
                <Row label="Second contact" value={me.contactPerson2Name} />
                <Row label="Second contact email" value={me.contactPerson2Email} />
                <Row label="Second contact mobile" value={me.contactPerson2Mobile} />
              </>
            )}
          </Card>

          <Card tour="vendor-profile-security" title="Sign-in and security" accent="#ef4444" right={<Button size="small" onClick={() => { setPwOpen(true); setPwError(''); }} sx={{ textTransform: 'none', fontWeight: 700 }}>Change password</Button>}>
            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b', mb: 1 }}>Your last sign-ins. If one is not you, change your password.</Typography>
            {logins.length === 0 ? (
              <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#94a3b8', py: 1 }}>No sign-ins recorded yet.</Typography>
            ) : logins.map((l, i) => (
              <Box key={l._id || i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, py: 0.9, borderBottom: i < logins.length - 1 ? '1px dashed #e2e8f0' : 'none' }}>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700 }}>{fmtTime(l.createdAt)}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b', textAlign: 'right' }}>{l.ipAddress || 'Unknown address'}</Typography>
              </Box>
            ))}
          </Card>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <Card tour="vendor-profile-business" title="Business" accent="#0ea5e9" right={<Button size="small" onClick={() => navigate('/dashboard')} sx={{ textTransform: 'none', fontWeight: 700 }}>Edit locations</Button>}>
            <Row label="Business name" value={me.business_name} />
            <Row label="GST number" value={me.gstNumber} mono />
            <Row label="PAN" value={me.businessPAN} mono />
            <Row label="Seller licence" value={me.sellerLicenseNumber} />
            <Row label="Business address" value={joinedAddress(me.businessAddress, me.businessCity, me.businessState, me.businessPincode)} />
            <Row label="Store location" value={joinedAddress(me.storeAddress, me.storeCity, me.storeState, me.storePincode)} />
          </Card>

          <Card tour="vendor-profile-bank" title="Bank and commission" accent="#22c55e">
            {me.bankDetails ? (
              <>
                <Row label="Account holder" value={me.bankDetails.accountHolder} />
                <Row label="Bank" value={me.bankDetails.bankName} />
                <Row label="Account number" value={me.bankDetails.accountNumberMasked} mono />
                <Row label="IFSC" value={me.bankDetails.ifsc} mono />
                {me.bankDetails.upiId && <Row label="UPI" value={me.bankDetails.upiId} />}
              </>
            ) : <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#94a3b8', py: 1 }}>No bank details on file.</Typography>}
            <Row label="Commission" value={me.commissionPercentage !== undefined && me.commissionPercentage !== null ? `${me.commissionPercentage}%` : null} />
            <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#94a3b8', mt: 1 }}>To change bank details or commission, please contact support.</Typography>
          </Card>
        </Box>
      </Box>

      {/* edit profile */}
      <Dialog open={editOpen} onClose={() => !saving && setEditOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800 }}>Edit profile</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Avatar src={form.preview || undefined} sx={{ width: 72, height: 72, bgcolor: '#e0e7ff', color: '#3730a3', fontWeight: 800 }}>{(form.name || 'S').charAt(0).toUpperCase()}</Avatar>
            <Box>
              <Button component="label" size="small" startIcon={<MdPhotoCamera />} sx={{ textTransform: 'none', fontWeight: 700, border: '1px solid #cbd5e1', borderRadius: '10px', color: '#0f172a' }}>
                {form.preview ? 'Change photo' : 'Add photo'}<input hidden type="file" accept="image/*" onChange={pickPhoto} />
              </Button>
              <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#94a3b8', mt: 0.5 }}>JPG or PNG, under 5 MB</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <TextField size="small" label="Name *" value={form.name} onChange={(e) => { setForm({ ...form, name: e.target.value }); setFormError(''); }} />
            <TextField size="small" label="Email *" value={form.email} onChange={(e) => { setForm({ ...form, email: e.target.value }); setFormError(''); }} helperText="You sign in with this email" />
            <TextField size="small" label="Mobile" value={form.phoneNumber} onChange={(e) => { setForm({ ...form, phoneNumber: e.target.value }); setFormError(''); }} />
          </Box>
          {formError && <Alert severity="error" sx={{ mt: 2 }}>{formError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={save} disabled={saving} sx={yellowBtn}>{saving ? <CircularProgress size={18} /> : 'Save changes'}</Button>
        </DialogActions>
      </Dialog>

      {/* change password */}
      <Dialog open={pwOpen} onClose={() => !pwSaving && setPwOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800 }}>Change password</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, mt: 0.5 }}>
            <TextField size="small" label="Current password" type={show.old ? 'text' : 'password'} value={pw.oldPassword} onChange={(e) => { setPw({ ...pw, oldPassword: e.target.value }); setPwError(''); }}
              InputProps={{ endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setShow({ ...show, old: !show.old })}>{show.old ? <MdVisibilityOff /> : <MdVisibility />}</IconButton></InputAdornment> }} />
            <Box>
              <TextField fullWidth size="small" label="New password" type={show.next ? 'text' : 'password'} value={pw.newPassword} onChange={(e) => { setPw({ ...pw, newPassword: e.target.value }); setPwError(''); }}
                InputProps={{ endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setShow({ ...show, next: !show.next })}>{show.next ? <MdVisibilityOff /> : <MdVisibility />}</IconButton></InputAdornment> }} />
              {pw.newPassword && (
                <Box sx={{ mt: 0.75 }}>
                  <LinearProgress variant="determinate" value={(pwScore / 4) * 100} sx={{ height: 6, borderRadius: 3, background: '#eef2f7', '& .MuiLinearProgress-bar': { background: pwColor, borderRadius: 3 } }} />
                  <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: pwColor, fontWeight: 700, mt: 0.25 }}>{pwLabel}</Typography>
                </Box>
              )}
            </Box>
            <TextField size="small" label="Repeat new password" type={show.next ? 'text' : 'password'} value={pw.confirm} onChange={(e) => { setPw({ ...pw, confirm: e.target.value }); setPwError(''); }}
              error={!!pw.confirm && pw.confirm !== pw.newPassword} helperText={pw.confirm && pw.confirm !== pw.newPassword ? 'Does not match' : 'At least 8 characters. Mix letters, numbers and symbols.'} />
          </Box>
          {pwError && <Alert severity="error" sx={{ mt: 2 }}>{pwError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPwOpen(false)} disabled={pwSaving} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={changePassword} disabled={pwSaving} sx={yellowBtn}>{pwSaving ? <CircularProgress size={18} /> : 'Change password'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Profile;
